/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import type { FacetModel } from '@/api/types/browse-search-types';

/**
 * Input: `/b/beam-type/low-and-high-beams`
 *
 * Captures: `beam-type` and `low-and-high-beams`
 *
 * Inputs that don't match:
 *   - `/b/brand/sylvania-basic/beam-type/low-and-high-beams`
 *   - `/b/beam-type/low-and-high-beams~high-beam`
 */
const firstFacetRegex = /^\/b\/([^/]+)\/([^/~]+)$/;

/**
 * Search for the first facet's value in the response of product-shelves
 *
 * ⚠️ This logic is a temporal workaround until BE has the ability to read the values directly, instead of relying on FE.
 * It is not properly optimized, as we have to iterate through the facets and values, comparing strings, and most of the times it's not needed.
 */
export const getFirstFacetValue = (
  facets: FacetModel[] | undefined,
  botEnabledFacetPath: string | undefined
): string | undefined => {
  if (facets && botEnabledFacetPath) {
    const [, firstFacetName, firstFacetValueKey] = firstFacetRegex.exec(botEnabledFacetPath) ?? [];

    if (firstFacetName && firstFacetValueKey) {
      const facetAttribute =
        firstFacetName === 'brand'
          ? 'brand_name_fltr'
          : `${firstFacetName.replaceAll('-', '_')}_fltr`;

      const selectedFacet = facets.find((facet) => facet.facetInternalName === facetAttribute);

      if (selectedFacet?.facetValues) {
        const facetValue = selectedFacet.facetValues.find((facetValue) =>
          facetValue.botSeoUrl?.endsWith(botEnabledFacetPath)
        );

        return facetValue?.facetValueDisplayName;
      }
    }
  }

  return undefined;
};
